import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import SocialShare from './SocialShare';

export interface BlogPostProps {
  title: string;
  slug: string;
  date: string;
  author: string;
  authorRole: string;
  imageSrc: string;
  excerpt: string;
  content: React.ReactNode;
  tags: string[];
  relatedCities?: Array<{
    name: string;
    route: string;
  }>;
}

const BlogPostTemplate: React.FC<BlogPostProps> = ({
  title,
  slug,
  date,
  author,
  authorRole,
  imageSrc,
  excerpt,
  content,
  tags,
  relatedCities = [],
}: BlogPostProps) => {
  const canonicalUrl = `https://tilimousine.com/blog/${slug}`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    image: `https://tilimousine.com${imageSrc}`,
    datePublished: date,
    dateModified: date,
    author: {
      '@type': 'Person',
      name: author,
      jobTitle: authorRole
    },
    publisher: {
      '@type': 'Organization',
      name: 'TiLimousine',
      logo: {
        '@type': 'ImageObject',
        url: 'https://tilimousine.com/logo.png'
      }
    },
    description: excerpt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{title} | TiLimousine Blog</title>
        <meta name="description" content={excerpt} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "author": { "@type": "Person", "name": author },
            "datePublished": date,
            "image": imageSrc,
            "publisher": {
              "@type": "Organization",
              "name": "TiLimousine",
              "logo": {
                "@type": "ImageObject",
                "url": "https://tilimousine.com/images/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      <Navbar />
      
      <article className="container mx-auto px-4 py-16">
        {/* Breadcrumb Navigation */}
        <nav className="mb-8">
          <Link to="/" className="text-gold hover:text-gold/80">Home</Link>
          <span className="mx-2 text-gold">/</span>
          <Link to="/blog" className="text-gold hover:text-gold/80">Blog</Link>
          <span className="mx-2 text-gold">/</span>
          <span className="text-white">{title}</span>
        </nav>

        {/* Hero Image */}
        <div className="relative w-full h-[400px] mb-12 rounded-lg overflow-hidden">
          <img 
            src={imageSrc} 
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Article Header */}
        <header className="mb-12">
          <h1 className="text-4xl md:text-5xl font-cinzel text-gold mb-6">{title}</h1>
          <div className="flex items-center text-white/80 mb-4">
            <span>{new Date(date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
            <span className="mx-2">•</span>
            <span>{author}</span>
            <span className="mx-2">•</span>
            <span>{authorRole}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, idx) => (
              <span 
                key={idx}
                className="px-3 py-1 bg-gray-800 rounded-full text-sm text-gold"
              >
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Article Content */}
        <div className="prose prose-invert max-w-none mb-12">
          {content}
        </div>

        {/* Related Cities */}
        {relatedCities.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-cinzel text-gold mb-8">Explore Our Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedCities.map((city, idx) => (
                <Link 
                  key={idx}
                  to={city.route} 
                  className="bg-gray-900 p-6 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <h3 className="text-xl font-semibold text-gold mb-2">Chauffeur Service in {city.name}</h3>
                  <p className="text-white/80">Premium transportation in {city.name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Social Sharing */}
        <section className="mb-12">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Share This Article</h2>
          <SocialShare 
            url={canonicalUrl}
            title={title}
            description={excerpt}
          />
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-3xl font-cinzel text-gold mb-6">Ready to Experience Premium Transportation?</h2>
          <p className="text-xl mb-8">Book your luxury chauffeur service today</p>
          <Link 
            to="/contact" 
            className="inline-block bg-gold text-black px-8 py-3 rounded-full hover:bg-gold/80 transition-colors font-semibold"
          >
            Book Now
          </Link>
        </section>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostTemplate; 